import { isPostcode, trimPostcode } from '../form/submit';

/* ****************************************************
 * Format the shops coming from Contentful grouping them
 * via the Region parameter. Used inside a reduce.
 *
 * @param {array} acc - the recursive array of formatted shops
 * @param {object} item - iteration containing the shop itself
 *
 * @returns {array} the compiled array of shops by region
 *
 * [ { region: 'London', data: [{ shop1, shop2, shop3 }]},
 *  {region: 'South East', data: [{ shop34, shop33}]}, ...
 * ]
 * ************************************************* */
export const getShopByRegion = (acc, item) => {
  // Validate the input
  if (!item || !item.shop || !item.shop.region) return acc;

  let newAcc = acc;
  // Searching if a region is already defined, works only because we
  // know already the region names and the indexOf will return only
  // 1 answer. It would not work if the region where London, South London
  const position = acc.map((r) => r.region).indexOf(item.shop.region);
  // in case is a new region initialize the region entry
  if (position === -1) {
    newAcc = [...acc, { region: item.shop.region, data: [item] }];
  } else {
    // otherwise just modify the region
    const adjustedRegion = {
      region: item.shop.region,
      data: [...acc[position].data, item],
    };
    newAcc = [
      ...acc.slice(0, position),
      adjustedRegion,
      ...acc.slice(position + 1),
    ];
  }
  return newAcc;
};

/* ****************************************************
 * get distance between two coords {lat, lng} works on any
 * shere!
 *
 * @param {object} baseGeo - coord 1
 * @param {object} shopServGeo - coord 2
 *
 * @returns {number} distance in miles
 * ************************************************* */
export const getDistance = (baseGeo) => (shopServGeo) => {
  // radius of the Earth, please change this if you are planning to open a shelter
  // shop on Mars
  const R = 6371e3;
  // getting radians for latitude
  const phi1 = (baseGeo.latitude * Math.PI) / 180; // φ, λ in radians
  const phi2 = (shopServGeo.lat * Math.PI) / 180;
  // getting deltas
  const deltaphi = ((baseGeo.latitude - shopServGeo.lat) * Math.PI) / 180;
  const deltadelta = ((baseGeo.longitude - shopServGeo.lon) * Math.PI) / 180;

  // calculate the the 'great-circle' distance using the 'haversine' formula, this
  // is generally fine but it doesn't work for big mountains. since it considers the
  // earth flat... So don't use it to compute the closest shop in the Alps..
  const a =
    Math.sin(deltaphi / 2) * Math.sin(deltaphi / 2) +
    Math.cos(phi1) *
      Math.cos(phi2) *
      Math.sin(deltadelta / 2) *
      Math.sin(deltadelta / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const miles = 1609.344;
  const d = R * c; // in meters
  return d / miles; // converting in miles because we don't like the metric system
};

const orderShopFromBase = (position, shops) => {
  const getDistanceFromBase = getDistance(position);
  if (shops) {
    return shops
      .map((item) => item.data)
      .reduce((acc, val) => acc.concat(val), [])
      .map((item) => ({
        ...item,
        distance: getDistanceFromBase(item.shop.location),
      }))
      .sort((a, b) => a.distance - b.distance)
      .slice(0, 5);
  }
};

/* ****************************************************
 * Check the location using the browser navigator object
 *
 * @param {function} setGeoList - hook with the shop ordered
 * @param {function} setLocations - hook to update the map
 *  boundaries
 * @param {array} shops - list of shop to order
 *
 * triggers 2 side effects: setGeoList, setLocations
 *
 * @returns {null}
 * ************************************************* */
export const checkGeoLocation = (setGeoList, setLocations, shops) => {
  const error = { lat: null, lng: null, error: 'no geolocation' };
  if (navigator.geolocation) {
    navigator.geolocation.getCurrentPosition((geo) => {
      if (geo.coords) {
        const shopsOrdered = orderShopFromBase(geo.coords, shops);
        setGeoList(shopsOrdered);
        setLocations(shopsOrdered.map((s) => s.shop));
      }
    });
  }
};
export const getClosestShopFromGeo = (
  position,
  setGeoList,
  setLocations,
  shops
) => {
  const shopsOrdered = orderShopFromBase(
    { latitude: position.lat, longitude: position.lng },
    shops
  );
  setGeoList(shopsOrdered);
  setLocations(shopsOrdered.map((s) => s.shop));
};

export const queryForGa = (userLocation) => {
  const { searchType, submittedLocation } = userLocation;

  if (searchType === 'gps') return 'gps';

  return isPostcode(submittedLocation)
    ? trimPostcode(submittedLocation)
    : submittedLocation;
};
